import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_DISPLAY_CURRENCY,
  normalizeDisplayCurrency,
  type DisplayCurrency,
} from '../constants/displayCurrencies';
import { useAuth } from './useAuth';
import { formatPrice } from './useMarketQuotes';
import {
  convertAmount,
  loadEurRates,
  peekEurRates,
  type EurRateBook,
} from '../utils/displayPrice';

/**
 * Format cached market prices in the signed-in player's display currency.
 * Rates load for euros too: TCGPlayer quotes are dollars.
 */
export function useDisplayPrice() {
  const { user } = useAuth();
  const currency: DisplayCurrency = user
    ? normalizeDisplayCurrency(user.displayCurrency)
    : DEFAULT_DISPLAY_CURRENCY;
  const [book, setBook] = useState<EurRateBook | null>(() => peekEurRates());

  useEffect(() => {
    const cached = peekEurRates();
    if (cached) setBook(cached);
    let cancelled = false;
    loadEurRates().then((next) => {
      if (!cancelled && next) setBook(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const rateReady = book != null && (currency === 'EUR' || book.rates[currency] != null);
  const rateDate = book && rateReady ? book.date : null;

  /**
   * Amount in the display currency, or null when the marketplace amount must stay
   * in its source currency (rates not loaded, or `convertAmount` has no rate).
   */
  const convert = useCallback((amount: number, sourceCurrency = 'EUR'): number | null => {
    const source = sourceCurrency.trim().toUpperCase() || 'EUR';
    if (source === currency) return amount;
    if (!book) return null;
    return convertAmount(amount, source, currency, book.rates);
  }, [book, currency]);

  const format = useCallback((
    amount: number | null | undefined,
    sourceCurrency = 'EUR',
  ): string | null => {
    if (amount == null || !Number.isFinite(amount)) return null;
    const source = sourceCurrency.trim().toUpperCase() || 'EUR';
    const converted = convert(amount, source);
    if (converted == null) return formatPrice(amount, source);
    return formatPrice(converted, currency);
  }, [convert, currency]);

  return {
    currency,
    convert,
    format,
    /**
     * True once euro rates are loaded. Euro display still needs them to convert
     * a dollar quote. Amounts can still fail when the source currency is missing.
     */
    converting: rateReady,
    rateDate,
  };
}
